import { useEffect, useState } from "react";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import Modal from "../../../layouts/Modal";
import LoadingWheel from "../LoadingWheel";
import useStripeCustom from "../../../data/useStripeCustom";
import { CheckoutSessionType } from "../../../models/Stripe";
import { ButtonInput } from "../Inputs/ButtonInput";
import StripeValidateModal from "./StripeValidateModal";
import { UseMutationResult } from "@tanstack/react-query";

import { useSearchParams } from 'react-router-dom';
import { UsageType } from "../../../models/UsageType";


// Initialize Stripe with your publishable key.
const stripePromise: Promise<Stripe | null> = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY!
);

interface StripeProductModalProps {
    open: boolean;
    setOpen: any;
    mutation: UseMutationResult<any, unknown, any, unknown> | null;
    sessionType: CheckoutSessionType;
}

export default function StripeProductModal({ open, setOpen, mutation, sessionType }: StripeProductModalProps) {
    const [quantity, setQuantity] = useState(1);
    const [isLoading, setIsLoading] = useState(true);

    const [price, setPrice] = useState<number>(0);
    const [productName, setProductName] = useState<string>("");
    const [productDescription, setProductDescription] = useState<string>("");
    const [minQuantity, setMinQuantity] = useState<number>(1);
    const stripeMinAmount = 0.3; // Minimum amount for Stripe transactions in GBP

    const { createSession, useGetPriceByUsageType, useGetProductByUsageType } = useStripeCustom();
    
    const [validateOpen, setValidateOpen] = useState(false);
    
    const [searchParams] = useSearchParams();
    
    const getUsageType = () => {
        switch (sessionType) {
            case CheckoutSessionType.LookupToken:
                return UsageType.PartLookup;
            case CheckoutSessionType.SmsToken:
                return UsageType.Sms;
            default:
                return null;
        }
    }

    const { data: usageData, isLoading: usageLoading } = useGetPriceByUsageType(getUsageType());
    const { data: productData, isLoading: productLoading } = useGetProductByUsageType(getUsageType());


    useEffect(() => {
        if (usageData && !usageLoading && productData && !productLoading) {
            // Assuming usageData contains a price field.
            const pricePerUnit = usageData.data?.unitAmount / 100 || 0;
            setPrice(pricePerUnit);
            setMinQuantity(pricePerUnit <= 0 ? 1 : Math.ceil(stripeMinAmount / pricePerUnit));
            setProductName(productData.data?.name || "-");
            setProductDescription(productData.data?.description || "-");
            setIsLoading(false);
        } else {
            setIsLoading(true);
        }
    }, [usageData, usageLoading, productData, productLoading]);

    useEffect(() => {
        if (open) setQuantity(minQuantity);
    }, [minQuantity]);
    

    const payNowClicked = () => {
        if (isLoading) return;

        // current base url
        const baseUrl = window.location.origin;

        const payload = {
            // TargetId: targetId,
            Type: sessionType,
            // The backend will set the Mode and additional fields.
            SuccessUrl: baseUrl,
            CancelUrl: baseUrl,

            quantity: quantity,
        };

        setIsLoading(true);
        // Call the API to create a Stripe Checkout session.
        createSession.mutate(payload, {
            onSuccess: async (data) => {
                console.log("Checkout session created:", data);
                // Data is expected to be: { sessionId, checkoutUrl }.
                const stripe = await stripePromise;
                if (stripe && data.data?.sessionId) {
                    const { error } = await stripe.redirectToCheckout({
                        sessionId: data.data.sessionId,
                    });
                    if (error) {
                        console.error("Stripe redirect error:", error.message);
                        // Optionally handle the error (e.g., display a message in the modal).
                    }
                } else {
                    console.error("Stripe not loaded or session ID missing");
                }
                setIsLoading(false);
            },
            onError: (error) => {
                console.error("Error creating checkout session:", error);
                setIsLoading(false);
                // Optionally update UI state to inform the user.
            },
        });
    };
    
    if (isLoading) {
        return (
            <Modal open={open} setOpen={setOpen} width={"max-w-2xl"}>
                <div className="py-10">
                    <LoadingWheel />
                </div>
            </Modal>
        );
    }



    return (
        <>
            {mutation && <StripeValidateModal mutation={mutation} /> }
            <Modal open={open} setOpen={setOpen} width={"max-w-md"}>
                <div className="text-center w-full px-5">
                    <h2 className="text-center text-primary text-xl font-bold">
                        Purchase {productName}s
                    </h2>
                    <div className="pt-4">
                        <p className="text-sm text-gray-500">
                            {productDescription}
                        </p>
                    </div>
                    <hr className="mt-4" />
                    <div className="pt-4">
                        <label htmlFor="quantity" className="block text-sm font-medium text-gray-700">
                            How many {productName}s would you like?
                        </label>
                        <input
                            type="number"
                            id="quantity"
                            value={quantity}
                            onChange={(e) => setQuantity(Number(e.target.value))}
                            min={minQuantity}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm text-center"
                        />
                        <small className="text-gray-500">
                            Each {productName} costs £{price.toFixed(2)} {minQuantity > 0 && `(Min of ${minQuantity} ${productName}s)`}.
                        </small>
                    </div>
                    <div className="text-primary text-4xl font-bold pt-4">
                        £{(quantity * price).toFixed(2)}
                    </div>

                    <div className="pt-8">
                        <ButtonInput
                            isSubmit={false}
                            label="Pay Now"
                            onClick={payNowClicked}
                            isLoading={isLoading}
                            classes="w-full"
                        />
                    </div>
                        

                </div>
            </Modal>
        </>
    );
    
}